import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { MapPin, Video, Clock, Check } from "lucide-react";

export const PackagesSection = ({ onPayment }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const packages = [
    {
      id: "1on1-coaching",
      name: "1:1 Regenerative Strength Coaching",
      location: "In-Person – Minnetonka, MN",
      locationType: "in-person",
      headline: "Build muscle. Improve body composition. Regulate stress and recovery. Age powerfully.",
      description: "For women 35+ who want muscle, metabolic resilience, and longevity.",
      includes: [
        "Personalized strength programming",
        "Macronutrient and body composition strategy",
        "HRV-guided training adjustments",
        "Recovery and nervous system optimization",
        "Lifestyle calibration for sustainable results"
      ],
      cta: "Book a Consultation",
      spotsLimited: true,
      featured: true
    },
    {
      id: "strategy-session",
      name: "Personal Optimization Strategy Session",
      location: "Zoom or In-Person",
      locationType: "hybrid",
      duration: "75–90 Minute Intensive",
      headline: "A deep dive into your training, macronutrients, wearable data, and recovery strategy.",
      description: "For those of you who want my personal systems, frameworks and methodology.",
      includes: [
        "Current training structure review",
        "Macronutrient and protein targets",
        "Wearable data interpretation",
        "Recovery strategy",
        "Fat loss and/or muscle-building direction"
      ],
      outcomes: [
        "Clear individualized macronutrient targets",
        "Weekly training direction",
        "Immediate priority next steps",
        "Recommended supplementation"
      ],
      cta: "Book a Consultation",
      featured: false
    },
    {
      id: "macro-data",
      name: "Macro + Data Coaching",
      location: "Zoom-Based Coaching",
      locationType: "virtual",
      sessions: "2 sessions per month",
      headline: "Data-driven decisions. No guesswork.",
      description: "Ongoing support via scheduled Zoom sessions using real-time data.",
      includes: [
        "Two Zoom sessions per month",
        "Custom macronutrient targets",
        "Weekly wearable and recovery review",
        "Data-driven training adjustments",
        "Structured accountability",
        "Messaging support between sessions"
      ],
      cta: "Book a Consultation",
      featured: false
    }
  ];

  const scrollToApply = () => {
    const element = document.getElementById("apply");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section 
      id="packages"
      ref={ref}
      className="py-24 md:py-40 px-6 md:px-12 bg-bone"
      data-testid="packages-section"
    >
      <div className="max-w-screen-2xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16 md:mb-24 text-center"
        >
          <p className="font-body text-xs tracking-[0.3em] uppercase text-graphite/50 mb-4">
            Coaching Options
          </p>
          <h2 
            className="font-heading text-4xl md:text-6xl font-bold tracking-tight uppercase text-graphite-dark"
            data-testid="packages-title"
          >
            Work with Elise
          </h2>
        </motion.div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {packages.map((pkg, index) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className={`relative bg-white border border-graphite/10 p-8 md:p-10 flex flex-col ${
                pkg.featured ? "lg:-mt-4 lg:mb-4 shadow-xl" : ""
              }`}
              data-testid={`package-card-${pkg.id}`}
            >
              {/* Spots Limited Badge */}
              {pkg.spotsLimited && (
                <div className="absolute top-0 right-0 bg-graphite-dark text-bone text-xs tracking-widest uppercase px-4 py-2">
                  Spots Limited
                </div>
              )}

              {/* Package Name */}
              <h3 className="font-heading text-xl md:text-2xl tracking-wide uppercase text-graphite-dark mb-4">
                {pkg.name}
              </h3>

              {/* Location */}
              <div className="flex items-center gap-2 mb-4 text-graphite/60">
                {pkg.locationType === "in-person" && <MapPin size={16} strokeWidth={1.5} />}
                {pkg.locationType === "virtual" && <Video size={16} strokeWidth={1.5} />}
                {pkg.locationType === "hybrid" && <Video size={16} strokeWidth={1.5} />}
                <span className="font-body text-sm">{pkg.location}</span>
              </div>

              {/* Details */}
              <div className="mb-6">
                {pkg.sessions && (
                  <p className="font-body text-sm text-graphite/60 mt-1">{pkg.sessions}</p>
                )}
                {pkg.duration && (
                  <p className="font-body text-sm text-graphite/60 mt-1 flex items-center gap-2">
                    <Clock size={14} strokeWidth={1.5} />
                    {pkg.duration}
                  </p>
                )}
              </div>

              {/* Headline */}
              <p className="font-body font-medium text-graphite-dark mb-2">
                {pkg.headline}
              </p>
              <p className="font-body text-sm text-graphite/70 mb-6">
                {pkg.description}
              </p>

              {/* Includes */}
              <div className="mb-8 flex-grow">
                <p className="font-body text-xs tracking-widest uppercase text-graphite/50 mb-3">
                  Includes
                </p>
                <ul className="space-y-2">
                  {pkg.includes.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check size={16} className="text-sand-dark mt-0.5 flex-shrink-0" strokeWidth={2} />
                      <span className="font-body text-sm text-graphite/80">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Outcomes (if applicable) */}
              {pkg.outcomes && (
                <div className="mb-8">
                  <p className="font-body text-xs tracking-widest uppercase text-graphite/50 mb-3">
                    You Leave With
                  </p>
                  <ul className="space-y-2">
                    {pkg.outcomes.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check size={16} className="text-sand-dark mt-0.5 flex-shrink-0" strokeWidth={2} />
                        <span className="font-body text-sm text-graphite/80">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* CTA Button */}
              <button
                onClick={scrollToApply}
                className="w-full bg-graphite-dark text-bone font-heading tracking-widest uppercase py-4 hover:bg-black transition-colors duration-300"
                data-testid={`package-cta-${pkg.id}`}
              >
                {pkg.cta}
              </button>

              {/* Venmo Option */}
              <button
                onClick={() => onPayment({ name: pkg.name, price: pkg.price })}
                className="w-full mt-3 border border-graphite/20 text-graphite font-heading text-sm tracking-widest uppercase py-3 hover:border-graphite/40 transition-colors duration-300"
                data-testid={`package-venmo-${pkg.id}`}
              >
                Pay with Venmo
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PackagesSection;
